import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ChevronsUpDown,
  Check,
  Plus,
  Download,
  EyeOff,
  CheckCircle,
  Link2,
} from "lucide-react";
import { FunctionComponent } from "react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { AlertDialogHeader, AlertDialogFooter } from "./ui/alert-dialog";

interface PlanContexMenuProps {
  plans: {
    id: string;
    name: string;
    is_primary?: boolean;
    is_visible?: boolean;
    is_template?: boolean;
  }[];
  selectedPlanId?: string;
  onPlanSelected?: (planId: string) => void;
  onNewPlanSelected?: () => void;
  onImportPlanSelected?: () => void;
  importLabel?: string;
  planType?: string;
  disabled?: boolean;
}

const PlanContexMenu: FunctionComponent<PlanContexMenuProps> = ({
  plans,
  selectedPlanId,
  onPlanSelected,
  onNewPlanSelected,
  onImportPlanSelected,
  importLabel,
  planType,
  disabled,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant={"ghost"} disabled={disabled}>
          <ChevronsUpDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        side={"right"}
        align="start"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {plans.map((plan) => (
            <DropdownMenuItem
              key={plan.id}
              onClick={() => onPlanSelected?.(plan.id)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <div className="w-4 flex-shrink-0">
                  {selectedPlanId === plan.id && <Check className="size-4" />}
                </div>
                <span>{plan.name}</span>
              </div>
              <div className="flex items-center gap-1">
                {plan.is_template && <Link2 className="size-4" />}
                {plan.is_primary && <CheckCircle className="size-4" />}
                {!plan.is_visible && <EyeOff className="size-4 " />}
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onNewPlanSelected}>
          <Plus className="size-5 mr-1" />
          Add {planType} plan
        </DropdownMenuItem>
        {onImportPlanSelected && (
          <DropdownMenuItem onClick={onImportPlanSelected}>
            <Download className="size-5 mr-1" />
            {importLabel ?? `Import ${planType} plan`}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface PlanChangeAlertProps {
  open: boolean;
  onClose: (canContinue: boolean) => void;
}

export const PlanChangeAlert: FunctionComponent<PlanChangeAlertProps> = ({
  open,
  onClose,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={() => onClose(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            This plan has unsaved changes. Are you sure you want to view another
            plan?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onClose(false)}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => onClose(true)}>
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PlanContexMenu;
